import { ColDef, ITooltipParams, ValueFormatterParams } from '@ag-grid-community/core';
import { Injectable } from '@angular/core';
import { FindingField, FindingStatus, HardwareState } from '@uigy/generated/graphql';
import { LanguageService } from '@uigy/shared/services/language.service';
import { GridFormatterService } from '@uigy/shared/modules/grid/formatters/grid-formatter.service';
import { useIpsKeyComponentMap } from '@uigy/shared/modules/grid/filters/selectable-filter/constants';
import { SelectableFilterFilterParams } from '@uigy/shared/modules/grid/filters/selectable-filter/interface';
import Utils from '@uigy/utils';
import { DateTimeService } from '@uigy/shared/services/date-time/date-time.service';
import { DatesFormatsEnum } from '@uigy/shared/enums/dateFormats.enum';
import { InventoryUiGridService } from '@uigy/modules/inventory/services/inventory-ui-grid.service';
import {
  VprCellRendererComponent,
} from '@uigy/shared/components/ui-grid/components/cell-renderers/vpr-cell-renderer/vpr-cell-renderer.component';
import { VprOptions } from '@uigy/shared/plugins-grid/enums/vpr.enum';
import { VprService } from '@uigy/shared/services/vpr.service';
import { PluginsGridSettingsService } from '@uigy/shared/plugins-grid/services/plugins-grid-settings.service';
import {
  SeverityCellRendererComponent,
} from '@uigy/shared/components/ui-grid/components/cell-renderers/severity-cell-renderer/severity-cell-renderer.component';
import { RISK_LEVELS, RISK_OFF_VALUE, RiskService } from '@uigy/shared/services/risk.service';
import { RiskScoreComponent } from '@uigy/shared/components/risk-widget/risk-score/risk-score.component';
import {
  RiskScoreCellRendererComponent,
} from '@uigy/shared/components/ui-grid/components/cell-renderers/risk-score-cell-renderer/risk-score-cell-renderer.component';
import { UiGridFilterModel } from '@uigy/shared/components/ui-grid/models/ui-grid';
import {
  SelectableFilterComponent,
} from '@uigy/shared/modules/grid/filters/selectable-filter/selectable-filter.component';
import { Cvss3ScoreService } from '@uigy/shared/services/cvss3-score.service';
import { Cvss3ScoreCategory } from '@uigy/shared/enums/cvss3-score.enum';
import { EndOfLifeService } from '@uigy/shared/services/end-of-life.service';
import { GridUtils } from '@uigy/shared/components/ui-grid/grid-utils';
import { AssetsDataHelperService } from '@uigy/modules/assets/services/assets-data-helper.service';
import {
  CriticalityCellRendererComponent,
} from '@uigy/shared/components/ui-grid/components/cell-renderers/criticality-cell-renderer/criticality-cell-renderer.component';
import {
  AssetTypeCellRendererComponent,
} from '@uigy/shared/components/ui-grid/components/cell-renderers/asset-type-cell-renderer/asset-type-cell-renderer.component';
import {
  LongTextCellRendererComponent,
} from '@uigy/shared/components/ui-grid/components/cell-renderers/long-text-cell-renderer/long-text-cell-renderer.component';
import {
  VendorRendererComponent,
} from '@uigy/shared/components/ui-grid/components/cell-renderers/vendor-renderer/vendor-renderer.component';
import {
  TimeFilterSelectorComponent,
} from '@uigy/shared/modules/grid/filters/time-filter/time-filter-selector.component';
import {
  createFilterComponentMap,
} from '@uigy/shared/modules/grid/filters/time-filter/time-filter-selector.interfaces';
import { CustomTimeFilterComponent } from '@uigy/shared/modules/grid/filters/time-filter/custom-time-filter/custom-time-filter.component';
import { TextListFilterComponent } from '@uigy/shared/modules/grid/filters/text-list-filter/text-list-filter.component';
import { TextListFilterParams } from '@uigy/shared/modules/grid/filters/text-list-filter/text-list-filter.interfaces';

@Injectable({
  providedIn: 'root',
})
export class VulnerabilityFindingsGridSettingsService {

  public readonly findingsTexts = this.languageService.texts.findings.vulnerability_findings.grid;
  public readonly sideDrawerText = this.languageService.texts.findings.vulnerability_findings.side_drawer.affected_asset;

  public get findingsColumnDefs() {
    return [
      this.FINDING_ID,
      this.AFFECTED_ASSET,
      this.IP,
      this.ASSET_MACS,
      this.SEVERITY,
      this.PLUGIN_NAME,
      this.PLUGIN_ID,
      this.PROTOCOL,
      this.PORT,
      this.VPR,
      this.PLUGIN_CVSS3_SCORE,
      this.STATUS,
      this.PLUGIN_SOURCE,
      this.FIRST_HIT,
      this.LAST_HIT,
      this.FIXED_AT,
      this.PLUGIN_FAMILY,
      this.ASSET_TYPE,
      this.ASSET_RISK,
      this.ASSET_CRITICALITY,
      this.ASSET_CATEGORY,
      this.ASSET_VENDOR,
      this.ASSET_FAMILY,
      this.ASSET_MODEL,
      this.ASSET_TAGS,
      this.FIRMWARE,
      this.EOL_STATE,
      this.EOL_DISCONTINUED_AT,
      this.EOL_VENDOR_STATUS,
      this.EOL_REPLACEMENT,
      this.OS,
      this.STATE,
      this.PURDUE,
      this.LOCATION,
      this.BACKPLANE,
    ];
  }

  private readonly FINDING_ID: ColDef = {
    colId: FindingField.FindingId,
    field: 'id',
    headerName: this.findingsTexts.id,
    hide: true,
    enableRowGroup: false,
    cellRenderer: 'copyTextRendererComponent',
    filter: TextListFilterComponent,
    filterParams: {
      title: this.findingsTexts.id,
    } as TextListFilterParams,
  };

  private readonly AFFECTED_ASSET: ColDef = {
    colId: FindingField.AssetName,
    field: 'asset.name',
    headerName: this.findingsTexts.affected_asset,
    minWidth: 200,
    valueGetter: params => params.data?.asset?.name ?? params.data?.assetName,
  };

  private readonly PLUGIN_NAME: ColDef = {
    colId: FindingField.PluginName,
    field: 'plugin.name',
    headerName: this.findingsTexts.plugin_name,
    minWidth: 296,
    valueGetter: params => params.data?.plugin?.name ?? params.data?.pluginName,
  };

  public readonly PLUGIN_ID: ColDef = {
    colId: FindingField.PluginId,
    field: 'plugin.id',
    headerName: this.findingsTexts.plugin_id,
    hide: true,
    width: 120,
    enableRowGroup: false,
    valueGetter: params => params.data?.plugin?.id ?? params.data?.pluginId,
    valueFormatter: params => params.data?.plugin.id.toString(),
  };


  private readonly IP: ColDef = {
    colId: FindingField.AssetIps,
    field: 'asset.ips',
    headerName: this.findingsTexts.ip,
    minWidth: 200,
    enableRowGroup: false,
    filter: SelectableFilterComponent,
    valueGetter: params => Utils.stripConnection(params?.data?.asset?.ips ?? params?.data?.assetIps),
    filterParams: {
      keyComponentMap: useIpsKeyComponentMap(),
    } as SelectableFilterFilterParams<any>,
  };

  private readonly SEVERITY: ColDef = {
    ...this.pluginsGridSettingsService.SEVERITY_COLUMN,
    colId: FindingField.PluginSeverity,
    sort: 'desc',
    field: 'plugin.severity',
    headerName: this.findingsTexts.severity,
    minWidth: 120,
    valueGetter: params => params.data?.plugin?.severity ?? params.data?.pluginSeverity,
    cellRenderer: SeverityCellRendererComponent,
    tooltipValueGetter: (params: ITooltipParams): string =>
      this.languageService.formatEnum('PluginSeverity', params.value) ?? '',
  };

  public readonly PROTOCOL: ColDef = {
    colId: FindingField.FindingProtocol,
    field: 'protocol',
    headerName: this.findingsTexts.protocol,
    minWidth: 152,
    valueGetter: params => params.data?.protocol ?? params.data?.findingProtocol,
    valueFormatter: (params: ValueFormatterParams): string => params.value?.toUpperCase(),
    filterParams: {
      valueFormatter: (value: string): string => value?.toUpperCase(),
    }
  };

  public readonly PORT: ColDef = {
    colId: FindingField.FindingPort,
    field: 'port',
    headerName: this.findingsTexts.port,
    minWidth: 200,
    valueGetter: params => {
      const port: number | undefined = params.data?.port ?? params.data?.findingPort;
      return port?.toString();
    },
  };

  private readonly VPR: ColDef = {
    headerName: this.findingsTexts.vpr,
    minWidth: 164,
    colId: FindingField.PluginVprScore,
    sort: 'desc',
    field: 'plugin.vprScore',
    cellRenderer: VprCellRendererComponent,
    enableRowGroup: false,
    valueGetter: params => params.data?.plugin?.vprScore ?? params.data?.pluginVprScore,
    tooltipValueGetter: (params: ITooltipParams): string =>
      this.vprService.getVprText(Number(params.value)),
    keyCreator: (params) => VprService.getVPRScoreOption(params.data?.vprScore),
    filterParams: {
      suppressSorting: true,
      values: Object.values(VprOptions),
      valueFormatter: (value: VprOptions) => this.gridFormatterService.VprTranslationFormatter(value)
    },
  };

  private readonly PLUGIN_CVSS3_SCORE: ColDef = {
    colId: FindingField.PluginCvss3Score,
    field: 'plugin.cvss3Score',
    headerName: this.findingsTexts.plugin_cvssv3_score,
    minWidth: 164,
    valueGetter: (params) => this.gridFormatterService.isInvalidNumber(params.data?.plugin?.cvss3Score ?? params.data?.pluginCvss3Score),
    enableRowGroup: false,
    filterParams: {
      suppressSorting: true,
      values: Object.values(Cvss3ScoreCategory),
      valueFormatter: (scoreCategory: Cvss3ScoreCategory) => this.gridFormatterService.formatCvssScoreCategoryTranslation(scoreCategory),
    },
  };

  private readonly STATUS: ColDef = {
    headerName: this.findingsTexts.status,
    minWidth: 152,
    colId: FindingField.FindingStatus,
    field: 'status',
    valueGetter: params => params.data?.status ?? params.data?.findingStatus,
    valueFormatter: (params: ValueFormatterParams): string => this.gridFormatterService.FindingStatusFormatter(params.value),
    filterParams: {
      suppressSorting: true,
      values: Object.values(FindingStatus),
      valueFormatter: this.gridFormatterService.FindingStatusFormatter,
    },
  };

  private readonly PLUGIN_SOURCE: ColDef = {
    headerName: this.findingsTexts.plugin_source,
    minWidth: 120,
    colId: FindingField.PluginSource,
    field: 'plugin.source',
    valueGetter: params => params.data?.plugin?.source ?? params.data?.pluginSource,
  };

  private readonly FIRST_HIT: ColDef = {
    headerName: this.findingsTexts.first_hit,
    field: FindingField.FindingFirstHit,
    minWidth: 200,
    hide: true,
    filter: TimeFilterSelectorComponent,
    enableRowGroup: false,
    valueGetter: (params) => params.data?.firstHit && DateTimeService.utcToLocalFormatted(params.data?.firstHit, DatesFormatsEnum.general),
    filterParams: {
      componentMap: createFilterComponentMap(),
    }
  };
  private readonly LAST_HIT: ColDef = {
    headerName: this.findingsTexts.last_hit,
    field: FindingField.FindingLastHit,
    minWidth: 200,
    filter: TimeFilterSelectorComponent,
    enableRowGroup: false,
    valueGetter: (params) => params.data?.lastHit && DateTimeService.utcToLocalFormatted(params.data?.lastHit, DatesFormatsEnum.general),
    filterParams: {
      componentMap: createFilterComponentMap(),
    }
  };
  private readonly FIXED_AT: ColDef = {
    headerName: this.findingsTexts.fixed_at,
    field: FindingField.FindingFixedAt,
    minWidth: 200,
    hide: true,
    filter: TimeFilterSelectorComponent,
    enableRowGroup: false,
    valueGetter: (params) => params.data?.fixedAt && DateTimeService.utcToLocalFormatted(params.data?.fixedAt, DatesFormatsEnum.general),
    filterParams: {
      componentMap: createFilterComponentMap(),
    }
  };

  private readonly PLUGIN_FAMILY: ColDef = {
    headerName: this.findingsTexts.plugin_family,
    colId: FindingField.PluginFamily,
    hide: true,
    field: 'plugin.family',
    minWidth: 152,
    valueGetter: params => params.data?.plugin?.family ?? params.data?.pluginFamily,
  };

  private readonly ASSET_FAMILY: ColDef = {
    headerName: this.findingsTexts.asset_family,
    colId: FindingField.AssetFamily,
    field: 'asset.family',
    minWidth: 152,
    valueGetter: params => params.data?.asset?.family ?? params.data?.assetFamily,
  };

  private readonly ASSET_TYPE: ColDef = {
    ...this.inventoryUiGridService.TYPE,
    headerName: this.findingsTexts.asset_type,
    colId: FindingField.AssetType,
    field: 'asset.type',
    minWidth: 200,
    valueGetter: params => params.data?.asset?.type ?? params.data?.assetType,
  };

  private readonly ASSET_RISK: ColDef = {
    headerName: this.findingsTexts.asset_risk_score,
    hide: true,
    colId: FindingField.AssetRisk,
    field: 'asset.risk.totalRisk',
    cellRenderer: RiskScoreCellRendererComponent,
    filterParams: {
      values: RISK_LEVELS.map(levelKey => this.languageService.texts.policies.policy_actions.event_severity.levels[levelKey].display),
      cellRenderer: RiskScoreComponent,
      suppressSorting: true,
      convertValuesToStrings: true,
    },
    valueGetter: params => params.data?.asset?.risk?.totalRisk ?? params.data?.assetRisk,
    tooltipValueGetter: (params: ITooltipParams): string =>
      +params.value === RISK_OFF_VALUE
        ? this.languageService.texts.general.risk.status.off
        : this.riskService.getRiskModel(params.value).text.display,
    enableRowGroup: false,
  };

  private readonly ASSET_CRITICALITY: ColDef = {
    ...this.inventoryUiGridService.CRITICALITY,
    headerName: this.findingsTexts.asset_criticality,
    minWidth: 120,
    colId: FindingField.AssetCriticality,
    field: 'asset.criticality',
    valueGetter: params => params.data?.asset?.criticality ?? params.data?.assetCriticality,
  };

  private readonly ASSET_CATEGORY: ColDef = {
    ...this.inventoryUiGridService.CATEGORY,
    headerName: this.findingsTexts.asset_category,
    minWidth: 152,
    colId: FindingField.AssetCategory,
    field: 'asset.category',
    hide: true,
    valueGetter: params => params.data?.asset?.category ?? params.data?.assetCategory,
  };

  private readonly ASSET_VENDOR: ColDef = {
    ...this.inventoryUiGridService.VENDOR,
    headerName: this.findingsTexts.asset_vendor,
    minWidth: 152,
    colId: FindingField.AssetVendor,
    field: 'asset.vendor',
    hide: true,
    valueGetter: params => params.data?.asset?.vendor ?? params.data?.assetVendor,
  };

  private readonly ASSET_MODEL: ColDef = {
    headerName: this.findingsTexts.asset_model,
    colId: FindingField.AssetModel,
    field: 'asset.model',
    minWidth: 152,
    hide: true,
    valueGetter: params => params.data?.asset?.model ?? params.data?.assetModel,
  };

  public readonly ASSET_MACS: ColDef = {
    headerName: this.findingsTexts.asset_mac,
    colId: FindingField.AssetMacs,
    field: 'asset.macs',
    minWidth: 200,
    valueFormatter: (params: ValueFormatterParams) => {
      const macs: string[] = Utils.stripConnection(params?.data?.asset?.macs);
      return macs.length > 0 ? this.assetsDataHelperService.formatAddresses(macs, macs): '';
    },
    filterParams: {
      valueFormatter: (macs: string) => macs === '' ? this.languageService.texts.general.key_words.blanks : macs,
      customMap: GridUtils.mapFlat,
    },
    enableRowGroup: false,
    hide: true,
  };

  private readonly FIRMWARE: ColDef = {
    headerName: this.findingsTexts.firmware,
    colId: FindingField.AssetFirmwareVersion,
    field: 'asset.firmwareVersion',
    minWidth: 120,
    valueGetter: params => params.data?.asset?.firmwareVersion ?? params.data?.assetFirmwareVersion,
  };

  private readonly OS: ColDef = {
    headerName: this.findingsTexts.os,
    colId: FindingField.AssetOs,
    field: 'asset.os',
    minWidth: 200,
    hide: true,
    valueGetter: params => params.data?.asset?.os ?? params.data?.assetOs,
  };

  private readonly BACKPLANE: ColDef = {
    ...this.inventoryUiGridService.BACKPLANE,
    headerName: this.findingsTexts.backplane_name,
    colId: FindingField.AssetBackplaneName,
    field: 'asset.backplane.name',
    valueGetter: params => params.data?.asset?.backplane?.name ?? params.data?.assetBackplaneName, // on groupBy it looks for assetBackplaneName
    minWidth: 200,
    hide: true,
  };

  private readonly STATE: ColDef = {
    headerName: this.findingsTexts.asset_state,
    colId: FindingField.AssetRunStatus,
    field: 'asset.runStatus',
    minWidth: 120,
    hide: true,
    valueGetter: params => params.data?.asset?.runStatus ?? params.data?.assetRunStatus,
  };

  private readonly PURDUE: ColDef = {
    ...this.inventoryUiGridService.PURDUE,
    headerName: this.findingsTexts.purdue_lvl,
    minWidth: 104,
    hide: true,
    colId: FindingField.AssetPurdueLevel,
    field: 'asset.purdueLevel',
    valueGetter: params => params.data?.asset?.purdueLevel ?? params.data?.assetPurdueLevel,
    valueFormatter: (params: ValueFormatterParams) => this.languageService.formatEnum('PurdueLevel', params.value),
  };


  public readonly LOCATION: ColDef = {
    headerName: this.findingsTexts.location,
    colId: FindingField.AssetLocation,
    field: 'asset.location',
    minWidth: 200,
    hide: true,
    valueGetter: params => params.data?.asset?.location ?? params.data?.assetLocation,
  };

  public readonly EOL_STATE: ColDef = {
    headerName: this.languageService.texts.inventory.grid_columns.eol_state,
    colId: FindingField.AssetHardwareState,
    field: 'asset.hardwareState',
    tooltipValueGetter: (params) => this.endOfLifeService.getEOLStateFromFinding(params.data),
    valueGetter: params => params.data?.asset?.hardwareState ?? params.data?.assetHardwareState,
    valueFormatter: (params) => this.endOfLifeService.getEOLStateFromFinding(params.data, params.node?.group),
    filterParams: {
      valueFormatter: (params: HardwareState) => this.endOfLifeService.hardwareStateFilterValues(params),
    },
    minWidth: 120,
    hide: true,
  };
  public readonly EOL_DISCONTINUED_AT: ColDef = {
    headerName: this.languageService.texts.inventory.grid_columns.eol_discontinued_at,
    field: FindingField.AssetDiscontinuedDate,
    minWidth: 120,
    hide: true,
    enableRowGroup: false,
    valueGetter: params => this.gridFormatterService.convertUtcToLocalStringFormatter(params.data?.asset.discontinuedDate),
    filter: CustomTimeFilterComponent,
    filterParams: {
      showTimeFilter: false,
    },
    comparator: Utils.sortByDate,
  };
  public readonly EOL_VENDOR_STATUS: ColDef = {
    headerName: this.languageService.texts.inventory.grid_columns.eol_vendor_status,
    field: FindingField.AssetLifecycleStatus,
    valueGetter: params => params.data?.asset?.lifecycleStatus,
    minWidth: 120,
    hide: true,
    enableRowGroup: false,
  };

  public readonly EOL_REPLACEMENT: ColDef = {
    headerName: this.languageService.texts.inventory.grid_columns.eol_replacement,
    field: FindingField.AssetReplacementProduct,
    valueGetter: params => params.data?.asset?.replacementProduct,
    minWidth: 120,
    hide: true,
    enableRowGroup: false,
  };

  private readonly ASSET_TAGS: ColDef = {
    ...this.inventoryUiGridService.TAGS,
    headerName: this.findingsTexts.asset_tags,
    colId: FindingField.AssetTags,
    field: 'asset.tags',
    valueGetter: params => params.data?.asset?.tags ?? params.data?.assetTags,
    minWidth: 200,
    hide: true,
    enableRowGroup: false,
    sortable: false,
  };

  // *** IMPORTANT ***
  // any other way to define grid settings (that are not readonly) cause weird flickering behaviour
  public readonly SIDE_DRAWER_AFFECTED_ASSET_GRID_SETTINGS = {
    components: {
      riskScoreRenderer: RiskScoreCellRendererComponent,
      criticalityRenderer: CriticalityCellRendererComponent,
      assetTypeRenderer: AssetTypeCellRendererComponent,
      assetVendorRenderer: VendorRendererComponent,
      longTextCellRenderer: LongTextCellRendererComponent,
    },
    columnDefs: [
      {
        ...this.AFFECTED_ASSET,
        headerName: this.sideDrawerText.name,
      },
      {
        ...this.ASSET_TYPE,
        headerName: this.sideDrawerText.type,
        cellRenderer: 'assetTypeRenderer',
      },
      {
        ...this.ASSET_CRITICALITY,
        headerName: this.sideDrawerText.criticality,
        cellRenderer: 'criticalityRenderer',
      },
      {
        ...this.ASSET_RISK,
        headerName: this.sideDrawerText.risk,
        hide: false,
        cellRenderer: 'riskScoreRenderer',
      },
      {
        ...this.IP,
        cellRenderer: 'longTextCellRenderer',
        headerName: this.sideDrawerText.ip,
      },
      {
        ...this.ASSET_MACS,
        cellRenderer: 'longTextCellRenderer',
        headerName: this.sideDrawerText.macs,
        hide: false,
      },
      {
        ...this.ASSET_VENDOR,
        headerName: this.sideDrawerText.vendor,
        hide: false,
        cellRenderer: 'assetVendorRenderer',
      },
      {
        ...this.ASSET_FAMILY,
        headerName: this.sideDrawerText.family,
      },
      {
        ...this.ASSET_MODEL,
        headerName: this.sideDrawerText.model,
        hide: false,
      },
      {
        ...this.FIRMWARE,
        headerName: this.sideDrawerText.firmware,
      },
      {
        ...this.OS,
        headerName: this.sideDrawerText.os,
        hide: false,
      },
      // {
      //   ...this.ASSET_TAGS,
      //   headerName: this.sideDrawerText.tags,
      //   hide: false,
      //
      // },
      {
        ...this.PURDUE,
        headerName: this.sideDrawerText.purdue,
        hide: false,
      },
    ],
  };

  constructor(
    private languageService: LanguageService,
    private inventoryUiGridService: InventoryUiGridService,
    private gridFormatterService: GridFormatterService,
    private pluginsGridSettingsService: PluginsGridSettingsService,
    private riskService: RiskService,
    private vprService: VprService,
    private cvss3ScoreService: Cvss3ScoreService,
    private endOfLifeService: EndOfLifeService,
    private assetsDataHelperService: AssetsDataHelperService,
  ) {
  }


  public findingsTransformFilterModel = (model: UiGridFilterModel<FindingField>): UiGridFilterModel<FindingField> => {
    switch (model.field) {
      case FindingField.AssetRisk:
        return this.riskService.getRiskScoreFilterModel(model, FindingField.AssetRisk, FindingField.AssetCriticality);
      case FindingField.PluginVprScore:
        return this.vprService.vprTransformFilterModel(model);
      case FindingField.PluginCvss3Score:
        return this.cvss3ScoreService.transformPluginCvss3ScoreUiGridFilterModel(model);
      case FindingField.AssetMacs:
      case FindingField.AssetSegments:
      case FindingField.AssetTags:
        return GridUtils.convertListFilter(model);
    }

    return model;
  };
}
